/**
 * Suggest per-node `inputMapping` patches when the author connects an edge
 * source → target. Emits Connect-mode JSONata refs (`formatTopLevelRef`).
 *
 *   - Concrete input schema (rag-retrieve `{ query }`): match by name +
 *     aliases (`answer` → `query`).
 *   - Open record types (llm-agent, mcp-query, …): passthrough — each upstream
 *     output field maps to the same-named input key.
 *
 * Never overwrites an existing key unless the ref is identical. Flags conflicts
 * when a key is already wired from a different *connected* upstream.
 *
 * Auto-wire on connect is skipped when the target already has Connect-mode refs
 * from an unplugged upstream and this is the sole incoming edge — unplug never
 * mutates mapping, and plugging a different node must not merge or override.
 */

import { formatTopLevelRef, parseTopLevelRef } from './jsonata-ref';

function refSourceKey(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  return parseTopLevelRef(value)?.sourceKey ?? null;
}

/**
 * True when the target takes the upstream node's whole output under one key
 * (`plan: "$.analyse"`, `dir: "$.directory"`). Everything that node emits is
 * wired already, so there is nothing to suggest, warn about or conflict with.
 */
function hasWholeNodeRef(existingMapping: Record<string, unknown>, sourceKey: string): boolean {
  return Object.values(existingMapping).some((value) => {
    const ref = parseTopLevelRef(value);
    return ref !== null && ref.sourceKey === sourceKey && ref.field === null;
  });
}

/**
 * Whether connecting `sourceKey` → target should mutate `inputMapping`.
 *   - Merge: other upstreams still connected → auto-wire new keys only.
 *   - Fresh: mapping empty → auto-wire.
 *   - Replace after unplug: mapping still references an unplugged upstream →
 *     leave mapping untouched (author clears or edits in Inputs).
 */
export function shouldAutoWireOnConnect(args: {
  sourceKey: string;
  existingMapping: Record<string, unknown>;
  connectedSourcesToTarget: ReadonlySet<string>;
}): boolean {
  const { sourceKey, existingMapping, connectedSourcesToTarget } = args;
  const otherConnected = [...connectedSourcesToTarget].filter((s) => s !== sourceKey);
  if (otherConnected.length > 0) return true;

  if (Object.keys(existingMapping).length === 0) return true;

  for (const value of Object.values(existingMapping)) {
    const src = refSourceKey(value);
    if (!src || src === '__input' || src === sourceKey) continue;
    if (!connectedSourcesToTarget.has(src)) return false;
  }
  return true;
}

export interface IWireConflict {
  inputKey: string;
  existing: string;
  proposed: string;
}

export interface IWireWarning {
  kind: 'unmapped-input' | 'no-upstream-fields';
  inputKey?: string;
  message: string;
}

export interface IAutoWireResult {
  /** New mapping entries to merge into the target node's inputMapping. */
  patches: Record<string, string>;
  conflicts: IWireConflict[];
  warnings: IWireWarning[];
}

/** Upstream output field names that can fill rag-retrieve's `query` input. */
const queryInputAliases = ['query', 'answer', 'text', 'prompt', 'question'];

function isUnset(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

function pickSourceField(inputKey: string, sourceFields: string[]): string | null {
  if (sourceFields.includes(inputKey)) return inputKey;
  if (inputKey === 'query') {
    for (const alias of queryInputAliases) {
      if (sourceFields.includes(alias)) return alias;
    }
  }
  return null;
}

function refsEqual(a: string, b: string): boolean {
  const pa = parseTopLevelRef(a);
  const pb = parseTopLevelRef(b);
  if (!pa || !pb) return a.trim() === b.trim();
  return pa.sourceKey === pb.sourceKey && pa.field === pb.field;
}

export function suggestAutoWire(args: {
  sourceKey: string;
  sourceFields: string[];
  targetInputFields: string[];
  existingMapping: Record<string, unknown>;
  /** Upstream node keys with an active edge into this target (incl. sourceKey). */
  connectedSourcesToTarget?: ReadonlySet<string>;
}): IAutoWireResult {
  const { sourceKey, sourceFields, targetInputFields, existingMapping, connectedSourcesToTarget } =
    args;
  const patches: Record<string, string> = {};
  const conflicts: IWireConflict[] = [];
  const warnings: IWireWarning[] = [];
  if (hasWholeNodeRef(existingMapping, sourceKey)) return { patches, conflicts, warnings };

  const tryAdd = (inputKey: string, sourceField: string) => {
    const proposed = formatTopLevelRef(sourceKey, sourceField);
    const existing = existingMapping[inputKey];
    if (existing === undefined || existing === null || existing === '') {
      patches[inputKey] = proposed;
      return;
    }
    if (typeof existing !== 'string') {
      conflicts.push({ inputKey, existing: JSON.stringify(existing), proposed });
      return;
    }
    // Leave advanced (non-Connect) expressions untouched.
    if (!parseTopLevelRef(existing)) return;
    if (refsEqual(existing, proposed)) return;
    const existingSource = refSourceKey(existing);
    if (
      existingSource &&
      existingSource !== sourceKey &&
      connectedSourcesToTarget &&
      !connectedSourcesToTarget.has(existingSource)
    ) {
      return;
    }
    conflicts.push({ inputKey, existing, proposed });
  };

  if (targetInputFields.length > 0) {
    for (const inputKey of targetInputFields) {
      const sourceField = pickSourceField(inputKey, sourceFields);
      if (sourceField) {
        tryAdd(inputKey, sourceField);
      } else if (isUnset(existingMapping[inputKey])) {
        warnings.push({
          kind: 'unmapped-input',
          inputKey,
          message:
            inputKey === 'query'
              ? 'Upstream has no compatible field for query (expected query, answer, text, prompt, or question).'
              : `Upstream has no field matching "${inputKey}".`,
        });
      }
    }
  } else {
    if (sourceFields.length === 0) {
      warnings.push({
        kind: 'no-upstream-fields',
        message: 'Upstream node has no output fields to wire into input mapping.',
      });
    }
    for (const field of sourceFields) {
      tryAdd(field, field);
    }
  }

  return { patches, conflicts, warnings };
}

/** True when the target already maps at least one expected key from this source. */
function hasWiringFromSource(args: {
  sourceKey: string;
  sourceFields: string[];
  targetInputFields: string[];
  existingMapping: Record<string, unknown>;
}): boolean {
  const { sourceKey, sourceFields, targetInputFields, existingMapping } = args;

  if (targetInputFields.length > 0) {
    for (const inputKey of targetInputFields) {
      const sourceField = pickSourceField(inputKey, sourceFields);
      if (!sourceField) continue;
      const existing = existingMapping[inputKey];
      if (
        typeof existing === 'string' &&
        refsEqual(existing, formatTopLevelRef(sourceKey, sourceField))
      ) {
        return true;
      }
    }
    return false;
  }

  for (const field of sourceFields) {
    const existing = existingMapping[field];
    if (typeof existing === 'string' && refsEqual(existing, formatTopLevelRef(sourceKey, field))) {
      return true;
    }
  }
  return false;
}

function detectIncompleteWiring(args: {
  sourceKey: string;
  sourceFields: string[];
  targetInputFields: string[];
  existingMapping: Record<string, unknown>;
}): IWireWarning[] {
  const { sourceFields, targetInputFields, existingMapping } = args;
  if (targetInputFields.length === 0) return [];

  const warnings: IWireWarning[] = [];
  for (const inputKey of targetInputFields) {
    const sourceField = pickSourceField(inputKey, sourceFields);
    if (sourceField && isUnset(existingMapping[inputKey])) {
      warnings.push({
        kind: 'unmapped-input',
        inputKey,
        message: `"${inputKey}" is not wired from this upstream node.`,
      });
    }
  }
  return warnings;
}

/**
 * Derive persisted edge wiring indicators from the current graph — used on
 * load, after inputMapping edits, and when schemas resolve.
 */
export function computeEdgeWiringData(args: {
  sourceKey: string;
  sourceFields: string[];
  targetInputFields: string[];
  existingMapping: Record<string, unknown>;
  connectedSourcesToTarget?: ReadonlySet<string>;
}): {
  autoWired: boolean;
  wiringConflicts: IWireConflict[];
  wiringWarnings: IWireWarning[];
} {
  if (hasWholeNodeRef(args.existingMapping, args.sourceKey)) {
    return { autoWired: true, wiringConflicts: [], wiringWarnings: [] };
  }
  const { patches, conflicts, warnings } = suggestAutoWire(args);

  if (conflicts.length > 0) {
    return { autoWired: false, wiringConflicts: conflicts, wiringWarnings: [] };
  }
  if (warnings.length > 0) {
    return { autoWired: false, wiringConflicts: [], wiringWarnings: warnings };
  }
  if (hasWiringFromSource(args)) {
    return { autoWired: true, wiringConflicts: [], wiringWarnings: [] };
  }

  const incomplete = detectIncompleteWiring(args);
  if (incomplete.length > 0) {
    return { autoWired: false, wiringConflicts: [], wiringWarnings: incomplete };
  }

  if (Object.keys(patches).length > 0) {
    return {
      autoWired: false,
      wiringConflicts: [],
      wiringWarnings: [
        {
          kind: 'unmapped-input',
          message: 'Upstream output is not wired into input mapping.',
        },
      ],
    };
  }

  return { autoWired: false, wiringConflicts: [], wiringWarnings: [] };
}
